import { useState, useEffect } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useIsAdmin } from '@/features/auth/hooks';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AgentRunCard, formatRunNumber } from '@/components/AgentRunCard';
import { AgentPollingPanel } from '@/components/AgentPollingPanel';
import { ApiError } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { formatServerDateShort } from '@/lib/dates';
import { existingDossierReviewRunIdFromConflict } from '@/features/dossier-sim/api';
import {
  useDispatchDossierReview,
  useDossierReviewRun,
  useDossierReviewRuns,
} from '@/features/dossier-sim/hooks';
import type {
  DossierReviewRunDetail,
  DossierReviewRunSummary,
} from '@/features/dossier-sim/api';
import { DossierReviewResultRenderer } from '@/features/dossier-sim/components/DossierReviewResultRenderer';
import { ScrollToTopButton } from '@/components/ScrollToTopButton';
import { PageHero } from '@/components/layout/PageHero';
import { US_SUBMISSION_PATHWAYS } from '@/features/dossier/us-submission-pathways';
import { CA_SUBMISSION_PATHWAYS } from '@/features/dossier/ca-submission-pathways';
import { EU_SUBMISSION_PATHWAYS } from '@/features/dossier/eu-submission-pathways';
import type { SubmissionGroup } from '@/features/dossier/us-submission-pathways';
import { RunLoadingSkeleton } from '@/components/RunLoadingSkeleton';

/**
 * Submission Dossier Simulator route. URL:
 *
 *   /simulators/dossier[?run_id=<n>]
 *
 * Two modes (mirrors the regulatory-strategy reviewer's shape):
 *   - No run_id  → upload form (.pdf + country + submission-type
 *                  → POST dispatch).
 *   - run_id set → poll the run, show stepper while running, render
 *                  the full dossier review report once complete.
 */

const searchSchema = z.object({
  run_id: z.coerce.number().int().positive().optional().catch(undefined),
});

// Jurisdictions offered in the picker. Narrowed to US only — the backend
// (app/dossier_sim/registry.SUPPORTED) still accepts CA and EU and their
// prompt modules are intact, so re-opening a jurisdiction is one line here.
const COUNTRY_OPTIONS: { value: string; label: string }[] = [
  { value: 'US', label: 'United States — FDA' },
];

// Pathways by country key — drives the submission-type Select. Kept for all
// three so a country re-added above needs no further change.
const PATHWAYS_BY_COUNTRY: Record<string, SubmissionGroup[]> = {
  US: US_SUBMISSION_PATHWAYS,
  CA: CA_SUBMISSION_PATHWAYS,
  EU: EU_SUBMISSION_PATHWAYS,
};

/** Device pathways only. The simulator reviews an uploaded document with no
 *  product record behind it, so there is no ``product_type`` to scope the list
 *  by the way the real dossier dispatch does (``features/dossier/dispatch/us``
 *  filters on ``product.product_type``). Drug and biologic pathways —
 *  NDA/ANDA/BLA/biosimilar — are therefore filtered out here rather than
 *  removed from the shared pathway files, which the real dossier flow uses. */
function deviceGroupsFor(countryCode: string): SubmissionGroup[] {
  return (PATHWAYS_BY_COUNTRY[countryCode] ?? []).filter(
    (g) => g.productType === 'device',
  );
}

function firstOptionValue(groups: SubmissionGroup[]): string {
  const first = groups[0]?.options[0];
  return first?.value ?? '';
}

const DOSSIER_WORKFLOW_STEPS = [
  'Extract dossier document text',
  'Identify submission type + jurisdiction',
  'Query regulatory database + guidance',
  'Score dossier against agency readiness checklist',
  'Classify Critical / Major / Minor findings',
  'Render dossier review report',
] as const;

const DOSSIER_STEP_SECONDS = 14;

export const Route = createFileRoute('/simulators_/dossier')({
  validateSearch: searchSchema,
  component: DossierSimulatorPage,
});

function DossierSimulatorPage() {
  const { run_id: runId } = Route.useSearch();
  const navigate = useNavigate();
  const runQuery = useDossierReviewRun(runId ?? null);

  return (
    <>
      <PageHero
        title="Submission Dossier Simulator"
        subtitle="Pre-screen a draft submission dossier against the selected jurisdiction's regulatory readiness checklist."
      />

      <main className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 pt-8 pb-20 space-y-5">
        {runId === undefined && (
          <>
            <UploadPanel
              onDispatched={(newRunId) => {
                void navigate({
                  to: '/simulators/dossier',
                  search: { run_id: newRunId },
                });
              }}
            />
            <ReviewHistory
              onView={(id) =>
                void navigate({
                  to: '/simulators/dossier',
                  search: { run_id: id },
                })
              }
            />
          </>
        )}

        {runId !== undefined && runQuery.isLoading && (
          <RunLoadingSkeleton />
        )}

        {runId !== undefined && runQuery.isError && (
          <ErrorCard message="Could not load this dossier review. It may have been deleted, or you may not have access." />
        )}

        {runId !== undefined && runQuery.data && (
          <RunStateBody
            run={runQuery.data}
            onStartAnother={() => void navigate({ to: '/simulators/dossier' })}
          />
        )}
      </main>
    </>
  );
}

// ── Upload panel (no run_id mode) ───────────────────────────────────────

function UploadPanel({
  onDispatched,
}: {
  onDispatched: (runId: number) => void;
}) {
  const dispatch = useDispatchDossierReview();
  const isAdmin = useIsAdmin();
  const [country, setCountry] = useState<string>('US');
  const [submissionType, setSubmissionType] = useState<string>(() =>
    firstOptionValue(deviceGroupsFor('US')),
  );
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  // When the country changes, reset submission type to the first option of
  // the new country's pathways.
  useEffect(() => {
    setSubmissionType(firstOptionValue(deviceGroupsFor(country)));
  }, [country]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!file) {
      setError('Pick a .pdf file to upload.');
      return;
    }
    // Mirrors the server's 150 MB cap (backend/dossier_sim/router.py). The
    // server aborts an oversized upload mid-stream, which some browsers
    // surface as a vague network error after minutes of uploading — reject
    // here first, instantly and reliably.
    if (file.size > 150 * 1024 * 1024) {
      setError('This file is larger than the 150 MB limit.');
      return;
    }
    if (!submissionType) {
      setError('Select a submission type.');
      return;
    }
    try {
      const res = await dispatch.mutateAsync({ file, country, submissionType });
      onDispatched(res.dossier_review_run_id);
    } catch (err) {
      // Same document already under review (double-click, two tabs): open that
      // run rather than reporting an error.
      const existing = existingDossierReviewRunIdFromConflict(err);
      if (existing !== null) {
        onDispatched(existing);
        return;
      }
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Dispatch failed.',
      );
    }
  };

  const currentGroups = deviceGroupsFor(country);

  return (
    <section className="bg-white border border-line rounded-2xl p-7 shadow-card">
      <h2 className="text-[18px] font-bold text-navy m-0 mb-2">
        Upload dossier document
      </h2>
      <p className="text-[14px] text-muted leading-[1.55] m-0 mb-4">
        Pick the target jurisdiction and submission type, then upload your
        draft dossier as a{' '}
        <strong className="text-navy font-semibold">.pdf</strong>. An AI
        reviewer adversarially pre-screens the dossier for Critical / Major /
        Minor findings grounded in the selected country's regulatory record.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="text-[11px] font-bold uppercase tracking-[0.08em] text-muted">
              Jurisdiction
            </label>
            <Select
              value={country}
              onValueChange={(v) => setCountry(v)}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COUNTRY_OPTIONS.map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold uppercase tracking-[0.08em] text-muted">
              Submission type
            </label>
            <Select
              value={submissionType}
              onValueChange={(v) => setSubmissionType(v)}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {currentGroups.map((group) => (
                  <SelectGroup key={group.label}>
                    <SelectLabel>{group.label}</SelectLabel>
                    {group.options.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <input
            type="file"
            accept=".pdf,application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="flex-1 min-w-[280px] py-2.5 px-3 border border-line-strong rounded-md bg-white text-[14px] text-ink file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-electric/10 file:text-navy file:font-semibold file:text-[12px] file:cursor-pointer hover:file:bg-electric/15"
          />
          <Button
            type="submit"
            variant="brand"
            disabled={dispatch.isPending || !file || !isAdmin}
            title={!isAdmin ? 'Admin only' : undefined}
            className="min-w-[160px]"
          >
            {dispatch.isPending ? 'Running review…' : 'Run review'}
          </Button>
        </div>

        <p className="text-[12px] text-muted m-0">
          .pdf only. Max 150 MB.
        </p>

        {error && (
          <div
            role="alert"
            className="rounded-[10px] py-3 px-4 bg-state-error-bg border border-state-error-border text-state-error-text text-[14px] flex items-start gap-2.5"
          >
            <AlertCircle className="size-4 shrink-0 mt-0.5" />
            {error}
          </div>
        )}
      </form>
    </section>
  );
}

// ── Run-state body ──────────────────────────────────────────────────────

function RunStateBody({
  run,
  onStartAnother,
}: {
  run: DossierReviewRunDetail;
  onStartAnother: () => void;
}) {
  // The server's verdict that this in-flight run is orphaned: nothing will
  // move it on its own, polling has stopped, and the list shows it as
  // stalled — so the stepper would be a lie that never ends.
  if (run.stale) {
    return (
      <div className="bg-state-warning-bg border border-state-warning-border rounded-2xl p-6 text-state-warning-text">
        <div className="font-bold text-[17px] mb-1">This review stalled</div>
        <p className="text-[14px] m-0 mb-3">
          It stopped making progress and will not finish on its own. It will
          be marked failed the next time an admin on your team starts a
          review, or when the server restarts. Until then it stays listed as
          stalled.
        </p>
        <Button variant="outline" onClick={onStartAnother}>
          Run another dossier
        </Button>
      </div>
    );
  }

  if (run.status === 'queued' || run.status === 'running') {
    return (
      <>
        <AgentRunCard
          run={{
            id: run.id,
            run_number: run.run_number,
            status: run.status,
            started_at: run.started_at,
          }}
          agentLabel="Dossier review run"
        />
        <AgentPollingPanel
          startedAt={run.started_at}
          workflowSteps={DOSSIER_WORKFLOW_STEPS}
          stepSeconds={DOSSIER_STEP_SECONDS}
          title="Reviewing dossier"
          subtitle="Reading the document, consulting the regulatory database, and classifying findings against the agency's submission checklist."
        />
      </>
    );
  }

  if (run.status === 'failed') {
    return (
      <div className="bg-state-error-bg border-l-4 border-state-error-accent rounded-lg py-[18px] px-6 text-state-error-text">
        <h3 className="m-0 mb-1.5 text-[17px] font-bold">
          The review didn&rsquo;t complete
        </h3>
        <p className="text-[14px] leading-[1.55] m-0 mb-4">
          {run.error_message ||
            'The dossier reviewer did not complete. Try running it again with the same or a revised file.'}
        </p>
        <Button variant="outline" onClick={onStartAnother}>
          Run another dossier
        </Button>
      </div>
    );
  }

  if (run.dossier_review_response) {
    return (
      <>
        <AgentRunCard
          run={{
            id: run.id,
            run_number: run.run_number,
            status: run.status,
            started_at: run.started_at,
          }}
          agentLabel="Dossier review run"
        />
        <DossierReviewResultRenderer report={run.dossier_review_response} />
        <ScrollToTopButton />
      </>
    );
  }

  return (
    <div className="bg-state-warning-bg border border-state-warning-border rounded-2xl p-6 text-state-warning-text">
      <div className="font-bold text-[17px] mb-1">
        Run completed but no report was produced
      </div>
      <p className="text-[14px] m-0 mb-3">
        The agent finished but the response payload was missing or
        unparseable. Try uploading the document again.
      </p>
      <Button variant="outline" onClick={onStartAnother}>
        Run another dossier
      </Button>
    </div>
  );
}

function ErrorCard({ message }: { message: string }) {
  return (
    <div className="bg-state-error-bg border border-state-error-border rounded-2xl p-6 text-state-error-text">
      {message}
    </div>
  );
}

// ── Review list (upload page) ───────────────────────────────────────────

const COUNTRY_SHORT: Record<string, string> = {
  US: 'United States',
  CA: 'Canada',
  EU: 'European Union',
};

const RUN_STATUS_TONE: Record<string, string> = {
  completed: 'bg-state-success-bg text-state-success-text',
  failed: 'bg-state-error-bg text-state-error-text',
  running: 'bg-state-warning-bg-soft text-[#713f12]',
  queued: 'bg-line text-muted',
  // An in-flight row past the server's reap ceiling. Its status column still
  // says running/queued, but nothing will move it on its own and polling has
  // stopped for it — so showing the live amber badge would be a lie that
  // persists until the next dispatch reaps the row.
  stalled: 'bg-state-error-bg text-state-error-text',
};

/** The badge to show: the server's staleness verdict overrides the raw status. */
function runBadge(r: DossierReviewRunSummary): string {
  return r.stale ? 'stalled' : r.status;
}

/** Review list on the upload page — the company's 50 most recent runs, every
 *  status, newest first, so an in-flight review is always reachable. Scoped to
 *  the company server-side, so a teammate's runs appear here too — which is
 *  why the heading says "Company reviews" and each row names who dispatched
 *  it. Hidden entirely when there are none.
 *
 *  The rows scroll inside the card rather than growing it: the container sits
 *  inside the card's padding, so it's inset from the rounded corners and
 *  nothing clips. `overscroll-contain` stops the page scrolling once the list
 *  hits its end. Under ~8 runs there's no scrollbar at all. The in-flight run
 *  is always the top row (ordering is newest-first), so the row that matters
 *  is never scrolled out of view. */
function ReviewHistory({ onView }: { onView: (id: number) => void }) {
  const { data, isLoading } = useDossierReviewRuns();
  if (isLoading || !data || data.length === 0) return null;
  return (
    <section className="bg-white border border-line rounded-2xl p-7 shadow-card">
      <h2 className="text-[18px] font-bold text-navy m-0 mb-3">Company reviews</h2>
      <div className="border-t border-line max-h-[420px] overflow-y-auto overscroll-contain">
        {data.map((r: DossierReviewRunSummary) => (
          <button
            key={r.id}
            type="button"
            onClick={() => onView(r.id)}
            className="w-full flex items-center justify-between gap-3 py-3 px-1 text-left border-b border-line hover:bg-[#F8FAFC] transition-colors"
          >
            <div className="min-w-0">
              <div className="text-[14px] font-semibold text-ink truncate">
                <span className="text-muted font-normal [font-variant-numeric:tabular-nums] mr-2">
                  {r.run_number == null ? '—' : formatRunNumber(r)}
                </span>
                {r.filename || 'dossier'}
              </div>
              <div className="text-[12px] text-muted">
                {[
                  COUNTRY_SHORT[r.country ?? ''] || r.country,
                  r.submission_type,
                  formatServerDateShort(r.started_at),
                  r.dispatched_by,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            </div>
            <div className="flex items-center gap-2.5 shrink-0">
              <span
                className={cn(
                  'text-[10.5px] font-bold uppercase tracking-[0.05em] py-0.5 px-2 rounded-full',
                  RUN_STATUS_TONE[runBadge(r)] ?? 'bg-line text-muted',
                )}
              >
                {runBadge(r)}
              </span>
              <span className="text-electric text-[13px] font-semibold">
                View →
              </span>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}
